import { useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileCheck,
  Building2,
  User,
  Calendar,
  DollarSign,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Check,
  X,
  Download,
  FileSignature,
  ChevronDown,
  Share2,
  Link,
  Copy,
  CheckCircle,
  FileText,
  ChevronUp,
  Eye,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  Users,
  CreditCard,
  Truck,
  MessageSquare,
  Send
} from 'lucide-react';
import { quotesAPI, adminQuotesAPI, depositsAPI, paymentsAPI } from '../services/api';
import api from '../services/api';
import SignatureCapture from '../components/common/SignatureCapture';
import PayDepositModal from '../components/PayDepositModal';
import { useFeatureFlag } from '../context/featureFlagStore';
import useAuthStore from '../context/authStore';
import { useDisplay } from '../hooks/useDisplay';
import { formatDateTime, formatMoney } from '../utils/format';
import { notifyError } from '../context/toastStore';
import { QuoteLineSections, SalesRepCard, AboutCard, PurchaseOrderField, RecurringPhrase } from '../components/quotes/QuotePresentation';

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fromAdmin = searchParams.get('from') === 'admin';
  const d = useDisplay();
  const canDownloadPdf = useFeatureFlag('feature.quote_pdf');
  const approvalEnabled = useFeatureFlag('feature.quote_approval');
  const canComment = useFeatureFlag('feature.quote_comments');
  const { user, can } = useAuthStore();
  // Approving or rejecting commits the company to spend: `quotes: accept`
  // (Company Admin and Billing by default). The server refuses it otherwise.
  const canApprove = approvalEnabled && can('quotes', 'accept');
  const isInternalStaff = user?.isInternalStaff || false;

  // Modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [signature, setSignature] = useState({ type: null, data: '', isValid: false });
  const [consented, setConsented] = useState(false);
  const [customerPO, setCustomerPO] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareExpiresDays, setShareExpiresDays] = useState(30);
  const [useCustomTerms, setUseCustomTerms] = useState(false);
  const [customTerms, setCustomTerms] = useState('');
  const [defaultTerms, setDefaultTerms] = useState('');
  const [showTermsEditor, setShowTermsEditor] = useState(false);
  const [showPayDeposit, setShowPayDeposit] = useState(false);
  const [showCheckConfirmAuth, setShowCheckConfirmAuth] = useState(false);
  const [checkSubmittingAuth, setCheckSubmittingAuth] = useState(false);

  // Comments state (admin only)
  const [showCommentsSection, setShowCommentsSection] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);

  // Handle signature changes
  const handleSignatureChange = useCallback((sig) => {
    setSignature(sig);
  }, []);

  // Always show something immediately
  console.log('QuoteDetailPage rendering with id:', id);

  const { data, isLoading, error, isError, status } = useQuery({
    queryKey: ['quote', id],
    queryFn: async () => {
      console.log('Fetching quote with id:', id);
      const response = await quotesAPI.get(id);
      console.log('Quote response:', response);
      return response;
    },
    retry: false,
    enabled: !!id,
  });

  // Fetch opportunity statuses for admin dropdown
  const { data: statusesData } = useQuery({
    queryKey: ['opportunity-statuses'],
    queryFn: () => adminQuotesAPI.getOpportunityStatuses(),
    enabled: fromAdmin,
  });
  const opportunityStatuses = statusesData?.data?.statuses || [];

  // Fetch deposit status for accepted quotes
  const { data: depositData, refetch: refetchDeposit } = useQuery({
    queryKey: ['deposit', id],
    queryFn: async () => {
      try {
        const response = await depositsAPI.getStatus(id);
        return response.data;
      } catch (err) {
        // 404 means no deposit exists for this quote - that's fine
        if (err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!id && !!data?.data?.quote, // Only fetch when we have quote data
    retry: false,
  });
  const deposit = depositData?.deposit || null;

  // Fetch payment settings for deposit modal
  const { data: paymentSettingsData } = useQuery({
    queryKey: ['paymentSettings'],
    queryFn: () => paymentsAPI.getSettings(),
    enabled: !!deposit, // Only fetch when deposit exists
  });
  const paymentSettings = paymentSettingsData?.data?.settings;

  // Fetch comments for admin view
  const { data: commentsData, refetch: refetchComments } = useQuery({
    queryKey: ['admin-quote-comments', id],
    queryFn: () => adminQuotesAPI.getComments(id),
    enabled: !!id && fromAdmin,
  });

  // Fetch comments for customer/portal view
  const { data: portalCommentsData, refetch: refetchPortalComments } = useQuery({
    queryKey: ['quote-comments', id],
    queryFn: () => quotesAPI.getComments(id),
    enabled: !!id && !fromAdmin && canComment !== false,
  });

  const comments = fromAdmin
    ? (commentsData?.data?.comments || [])
    : (portalCommentsData?.data?.comments || []);
  const unreadCount = commentsData?.data?.unreadCount || 0;

  // Fetch linked ticket for admin view
  const { data: ticketData, refetch: refetchTicket } = useQuery({
    queryKey: ['admin-quote-ticket', id],
    queryFn: () => adminQuotesAPI.getQuoteTicket(id),
    enabled: !!id && fromAdmin,
  });
  const linkedTicket = ticketData?.data?.ticket;
  const ticketNotes = ticketData?.data?.notes || [];

  const quote = data?.data?.quote;
  // CW forecast API returns an object with forecastItems array inside
  const forecastData = data?.data?.lineItems;
  const lineItems = Array.isArray(forecastData?.forecastItems) ? forecastData.forecastItems : [];
  const forecastTotals = forecastData?.forecastRevenueTotals;
  // Use quote.totalSalesTax for precision (forecastData.salesTaxRevenue is rounded to integer)
  const salesTax = quote?.totalSalesTax || forecastData?.salesTaxRevenue || 0;
  const savedSignature = data?.data?.signature; // Signature from database (if quote was already approved)
  const customerViews = data?.data?.customerViews || []; // Detailed customer view history (excludes admin views)
  const customerViewStats = data?.data?.customerViewStats || {}; // Customer-only view stats
  const expirationDate = data?.data?.expirationDate; // Quote expiration date from custom field
  const isExpired = data?.data?.isExpired || false; // Whether the quote is expired
  const depositAmount = data?.data?.depositAmount; // Deposit amount from QuoteWerks
  const paymentTerms = data?.data?.paymentTerms; // Payment terms from QuoteWerks
  const shippingMethod = data?.data?.shippingMethod; // Shipping method from QuoteWerks
  const serviceDescription = data?.data?.serviceDescription; // Service description from QuoteWerks (shown on Service-type forecast items)
  // Sections, totals, the account manager and the purchase order rule (services/quotes/presentation.js).
  const presentation = data?.data?.presentation || null;
  const poMode = presentation?.poField || 'off';

  // Approve mutation - updates CW opportunity status to Won with e-signature
  const approveMutation = useMutation({
    mutationFn: async ({ notes, signature, consent, customerPO: po }) => {
      return api.post(`/quotes/${id}/approve`, { notes, signature, consent, ...(po ? { customerPO: po } : {}) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      setShowApproveModal(false);
      setApproveNotes('');
      setSignature({ type: null, data: '', isValid: false });
    },
  });

  // Reject mutation - updates CW opportunity status to Lost
  const rejectMutation = useMutation({
    mutationFn: async (reason) => {
      return api.post(`/quotes/${id}/reject`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      setShowRejectModal(false);
      setRejectReason('');
    },
  });

  // Admin status change mutation (for CW opportunities)
  const statusMutation = useMutation({
    mutationFn: (newStatus) => adminQuotesAPI.updateStatus(id, newStatus, 'opportunity'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      setShowStatusDropdown(false);
    },
    onError: (error) => {
      console.error('Status update failed:', error);
      alert('Failed to update status: ' + (error.response?.data?.error || error.message));
      setShowStatusDropdown(false);
    },
  });

  // Share link mutation
  const shareMutation = useMutation({
    mutationFn: async ({ expiresInDays, customTerms }) => {
      const response = await api.post('/quotes/session', {
        opportunityId: parseInt(id, 10),
        expiresInDays,
        customTerms
      });
      return response;
    },
    onSuccess: (response) => {
      const session = response?.data?.session;
      if (session?.accessToken) {
        setShareLink({
          url: session.url || `${window.location.origin}/quote/${session.accessToken}`,
          expiresAt: session.expiresAt,
        });
      }
    },
    onError: (error) => {
      console.error('Failed to create share link:', error);
      alert('Failed to create share link: ' + (error.response?.data?.error || error.message));
    },
  });

  // Comment mutations (admin)
  const addCommentMutation = useMutation({
    mutationFn: (data) => adminQuotesAPI.addComment(id, data),
    onSuccess: () => {
      refetchComments();
      setReplyText('');
      setReplyingTo(null);
    },
  });

  // Comment mutation (portal customer)
  const addPortalCommentMutation = useMutation({
    mutationFn: (message) => quotesAPI.addPortalComment(id, message),
    onSuccess: () => {
      refetchPortalComments();
      setReplyText('');
    },
  });

  // Add note to linked ticket (if exists)
  const addTicketNoteMutation = useMutation({
    mutationFn: (message) => adminQuotesAPI.addTicketNote(id, message),
    onSuccess: () => {
      refetchTicket();
      refetchComments();
      setReplyText('');
      setReplyingTo(null);
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => adminQuotesAPI.markAllCommentsRead(id),
    onSuccess: () => {
      refetchComments();
    },
  });

  // Handle expanding comments section
  const handleExpandComments = () => {
    setShowCommentsSection(!showCommentsSection);
    if (!showCommentsSection && unreadCount > 0) {
      markAllReadMutation.mutate();
    }
  };

  const handleSubmitReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    if (!fromAdmin) {
      // Portal customer submitting a question
      addPortalCommentMutation.mutate(replyText);
    } else if (linkedTicket) {
      // Admin with linked ticket - add note to ticket
      addTicketNoteMutation.mutate(replyText);
    } else if (replyingTo) {
      // Admin without ticket - fall back to comment system
      addCommentMutation.mutate({
        sessionId: replyingTo.sessionId,
        message: replyText,
        parentId: replyingTo.commentId,
      });
    }
  };

  const handleApprove = () => {
    if (!signature.isValid || !consented || (poMode === 'required' && !customerPO.trim())) {
      return;
    }
    approveMutation.mutate({
      notes: approveNotes,
      consent: true,
      customerPO: poMode !== 'off' ? customerPO.trim() : '',
      signature: {
        type: signature.type,
        data: signature.data
      }
    });
  };

  const handleReject = () => {
    rejectMutation.mutate(rejectReason);
  };

  const handleCopyLink = async () => {
    if (shareLink?.url) {
      try {
        await navigator.clipboard.writeText(shareLink.url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
        notifyError('The link could not be copied.', err);
      }
    }
  };

  const handleShare = async () => {
    setShareLink(null);
    setLinkCopied(false);
    setShareExpiresDays(30);
    setUseCustomTerms(false);
    setShowTermsEditor(false);

    // Fetch default terms
    try {
      const response = await api.get('/quotes/default-terms');
      const terms = response?.data?.terms || '';
      setDefaultTerms(terms);
      setCustomTerms(terms);
    } catch (err) {
      console.warn('Could not fetch default terms:', err);
      // Use a fallback if API fails
      const fallbackTerms = 'By accepting this quote, you agree to the terms and conditions.';
      setDefaultTerms(fallbackTerms);
      setCustomTerms(fallbackTerms);
    }

    setShowShareModal(true);
  };

  const handleGenerateShareLink = () => {
    shareMutation.mutate({
      expiresInDays: shareExpiresDays,
      customTerms: useCustomTerms ? customTerms : null
    });
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob' });

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create temp link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${id}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notifyError('The PDF could not be downloaded.', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Check if quote is editable (not already won/lost/accepted/rejected)
  const statusName = quote?.status?.name?.toLowerCase() || '';
  const closedStatuses = ['won', 'lost', 'accepted from portal', 'rejected from portal', 'accepted', 'rejected'];
  const isEditable = quote && !closedStatuses.some(s => statusName.includes(s));

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle date-only strings (YYYY-MM-DD) by parsing as local date to avoid timezone shift
      // Adding T12:00:00 ensures the date won't shift regardless of timezone
      const date = dateString.includes('T')
        ? new Date(dateString)
        : new Date(dateString + 'T12:00:00');
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '$0.00';
    try {
      return formatMoney(parseFloat(price));
    } catch {
      return '$0.00';
    }
  };

  // What is due now: one-time lines, shipping and tax. Recurring charges are
  // shown apart; ConnectWise's own forecast total counts them twice.
  const dueNow = () => (presentation ? presentation.totals.total : calculateTotal() + salesTax);

  const calculateTotal = () => {
    // Use forecastTotals if available, otherwise sum up line items
    if (forecastTotals?.revenue) {
      return parseFloat(forecastTotals.revenue) || 0;
    }
    try {
      return lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.revenue) || 0);
      }, 0);
    } catch {
      return 0;
    }
  };

  // Back button component for reuse
  const BackButton = () => (
    <button
      onClick={() => {
        if (fromAdmin) {
          const returnUrl = sessionStorage.getItem('adminQuotesReturnUrl') || '/admin/quotes';
          sessionStorage.removeItem('adminQuotesReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        } else {
          const returnUrl = sessionStorage.getItem('quotesReturnUrl') || '/quotes';
          sessionStorage.removeItem('quotesReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        }
      }}
      className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-6"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to Quotes
    </button>
  );

  // Loading state
  if (isLoading || status === 'pending') {
    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading quote {id}...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || error || !quote) {
    const errorMessage = error?.response?.data?.error || error?.message || 'This quote could not be loaded.';
    console.error('Quote error:', error, 'Status:', status, 'Data:', data);

    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">Quote Not Found</h2>
          <p className="text-red-700 dark:text-red-300">{errorMessage}</p>
          <p className="text-sm text-red-500 dark:text-red-400 mt-2">Quote ID: {id}</p>
        </div>
      </div>
    );
  }

  // Success - render quote details
  const getStatusBadgeClass = (statusName) => {
    const lowerStatus = (statusName || '').toLowerCase();
    if (lowerStatus.includes('won') || lowerStatus.includes('accepted')) {
      return 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300';
    } else if (lowerStatus.includes('lost')) {
      return 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300';
    } else if (lowerStatus.includes('open')) {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300';
    }
    return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300';
  };

  const currentStatusName = quote.status?.name || 'Open';
  const badgeClass = getStatusBadgeClass(currentStatusName);

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <BackButton />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{quote.name || 'Quote'}</h1>
            <p className="text-neutral-500 dark:text-neutral-400">Quote #{id}</p>
          </div>
          {fromAdmin && opportunityStatuses.length > 0 ? (
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 ${badgeClass}`}
                disabled={statusMutation.isPending}
              >
                {statusMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                {currentStatusName}
                <ChevronDown className="w-3 h-3" />
              </button>
              {showStatusDropdown && (
                <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 z-10 max-h-64 overflow-y-auto">
                  {opportunityStatuses.map((status) => (
                    <button
                      key={status.id}
                      onClick={() => statusMutation.mutate(status.name)}
                      className={`w-full text-left px-4 py-2 text-sm text-neutral-900 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 ${
                        currentStatusName === status.name ? 'bg-neutral-100 dark:bg-neutral-700 font-medium' : ''
                      }`}
                    >
                      {status.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeClass}`}>
              {currentStatusName}
            </span>
          )}
        </div>
      </div>

      {/* Expired Warning Banner */}
      {isExpired && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-red-800 dark:text-red-200 font-medium">This quote has expired</p>
            <p className="text-red-600 dark:text-red-300 text-sm">
              This quote expired on {formatDate(expirationDate)} and can no longer be accepted. Please contact us for an updated quote.
            </p>
          </div>
        </div>
      )}

      {/* Quote Info */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
        <div className="grid sm:grid-cols-2 gap-6">
          {d.visible('quoteDetail.info.company') && (
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.info.company')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">{quote.company?.name || 'N/A'}</div>
            </div>
          </div>
          )}
          {d.visible('quoteDetail.info.contact') && (
          <div className="flex items-start gap-3">
            <User className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.info.contact')}</div>
              <div className="font-medium text-neutral-900 dark:text-white">
                {quote.contact?.name || 'N/A'}
              </div>
            </div>
          </div>
          )}
          {d.visible('quoteDetail.info.paymentTerms') && paymentTerms && (
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.info.paymentTerms')}</div>
                <div className="font-medium text-neutral-900 dark:text-white">{paymentTerms}</div>
              </div>
            </div>
          )}
          {d.visible('quoteDetail.info.shippingMethod') && shippingMethod && (
            <div className="flex items-start gap-3">
              <Truck className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.info.shippingMethod')}</div>
                <div className="font-medium text-neutral-900 dark:text-white">{shippingMethod}</div>
              </div>
            </div>
          )}
          {fromAdmin && (
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Expected Close Date</div>
                <div className="font-medium text-neutral-900 dark:text-white">{formatDate(quote.expectedCloseDate)}</div>
              </div>
            </div>
          )}
          {fromAdmin && (
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Total Value</div>
                <div className="font-medium text-neutral-900 dark:text-white">{formatPrice(presentation ? presentation.totals.oneTime : calculateTotal())}</div>
                {presentation?.totals.recurring > 0 && (
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">plus <RecurringPhrase totals={presentation.totals} /></div>
                )}
              </div>
            </div>
          )}
          {d.visible('quoteDetail.info.expires') && expirationDate && (
            <div className="flex items-start gap-3">
              <Clock className={`w-5 h-5 mt-0.5 ${isExpired ? 'text-red-500' : 'text-neutral-400'}`} />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.info.expires')}</div>
                <div className={`font-medium ${isExpired ? 'text-red-600 dark:text-red-400' : 'text-neutral-900 dark:text-white'}`}>
                  {formatDate(expirationDate)}
                  {isExpired && <span className="ml-2 text-xs font-semibold text-red-500">(EXPIRED)</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        {quote.notes && (
          <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">Notes</div>
            <p className="text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}
      </div>

      {(presentation?.salesRep || presentation?.about) && (
        <div className="grid gap-6 md:grid-cols-3 mb-6">
          <AboutCard about={presentation.about} className={presentation.salesRep ? 'md:col-span-2' : 'md:col-span-3'} />
          <SalesRepCard rep={presentation.salesRep} className={presentation.about ? '' : 'md:col-span-3'} />
        </div>
      )}

      {/* Line Items */}
      {d.visible('quoteDetail.section.lineItems') && (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">{d.label('quoteDetail.section.lineItems')}</h2>
        </div>

        {presentation ? (
          <QuoteLineSections
            presentation={presentation}
            depositAmount={d.visible('quoteDetail.lineItems.depositRequired') ? depositAmount || 0 : 0}
            labels={{
              item: d.label('quoteDetail.lineItems.col.item'),
              qty: d.label('quoteDetail.lineItems.col.qty'),
              price: d.label('quoteDetail.lineItems.col.price'),
              total: d.label('quoteDetail.lineItems.col.total'),
            }}
          />
        ) : lineItems.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
            <FileCheck className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p>No line items in this quote</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.lineItems.col.item')}</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.lineItems.col.qty')}</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.lineItems.col.price')}</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">{d.label('quoteDetail.lineItems.col.total')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {lineItems.map((item, index) => {
                  const unitPrice = item.quantity > 0 ? (item.revenue / item.quantity) : item.revenue;
                  const isService = item.forecastType === 'Service';
                  // Prefer productDescription (full-length, no cap) when populated.
                  // Only fall back to the Service Description custom field for
                  // Service items whose productDescription is empty — the
                  // original Fixed Fee Service (no catalog link) case. The
                  // custom field is capped at 1000 chars, so preferring it when
                  // productDescription is longer would silently truncate.
                  const lineDescription =
                    item.productDescription ||
                    (isService && serviceDescription) ||
                    null;
                  return (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-neutral-900 dark:text-white">
                          {item.catalogItem?.description || item.catalogItem?.identifier || item.forecastDescription || 'Item'}
                        </div>
                        {lineDescription && (
                          <div className="text-sm text-neutral-500 dark:text-neutral-400 whitespace-pre-line">{lineDescription}</div>
                        )}
                        <div className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">{item.forecastType}</div>
                      </td>
                      <td className="px-4 py-4 text-center text-neutral-600 dark:text-neutral-300">
                        {item.quantity || 1}
                      </td>
                      <td className="px-4 py-4 text-right text-neutral-600 dark:text-neutral-300">
                        {formatPrice(unitPrice)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-neutral-900 dark:text-white">
                        {formatPrice(item.revenue)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {d.visible('quoteDetail.lineItems.subtotal') && (
                <tr className="border-t border-neutral-200 dark:border-neutral-700">
                  <td colSpan="3" className="px-6 py-2 text-right text-neutral-600 dark:text-neutral-300">
                    {d.label('quoteDetail.lineItems.subtotal')}
                  </td>
                  <td className="px-6 py-2 text-right font-medium text-neutral-900 dark:text-white">
                    {formatPrice(calculateTotal())}
                  </td>
                </tr>
                )}
                {d.visible('quoteDetail.lineItems.salesTax') && salesTax > 0 && (
                  <tr>
                    <td colSpan="3" className="px-6 py-2 text-right text-neutral-600 dark:text-neutral-300">
                      {d.label('quoteDetail.lineItems.salesTax')}
                    </td>
                    <td className="px-6 py-2 text-right font-medium text-neutral-900 dark:text-white">
                      {formatPrice(salesTax)}
                    </td>
                  </tr>
                )}
                {d.visible('quoteDetail.lineItems.total') && (
                <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                  <td colSpan="3" className="px-6 py-4 text-right font-medium text-neutral-900 dark:text-white">
                    {d.label('quoteDetail.lineItems.total')}
                  </td>
                  <td className="px-6 py-4 text-right text-xl font-bold text-primary-600 dark:text-primary-400">
                    {formatPrice(calculateTotal() + salesTax)}
                  </td>
                </tr>
                )}
                {d.visible('quoteDetail.lineItems.depositRequired') && depositAmount && depositAmount > 0 && (
                  <tr className="border-t border-neutral-200 dark:border-neutral-700">
                    <td colSpan="3" className="px-6 py-3 text-right text-neutral-600 dark:text-neutral-300">
                      <span className="flex items-center justify-end gap-2">
                        <CreditCard className="w-4 h-4" />
                        {d.label('quoteDetail.lineItems.depositRequired')}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-semibold text-amber-600 dark:text-amber-400">
                      {formatPrice(depositAmount)}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        )}
      </div>
      )}

      {/* E-Signature Section */}
      {savedSignature && (
        <div className="mt-6 bg-white dark:bg-neutral-800 rounded-lg border border-green-200 dark:border-green-800">
          <div className="px-6 py-4 border-b border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30">
            <div className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-green-900 dark:text-green-300">E-Signature</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Signature Display */}
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">Signature</p>
                <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-700/50">
                  {savedSignature.signature_type === 'drawn' && savedSignature.signature_data ? (
                    <img
                      src={savedSignature.signature_data}
                      alt="Signature"
                      className="max-h-24"
                    />
                  ) : (
                    <p
                      className="text-2xl text-neutral-800 dark:text-neutral-200"
                      style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}
                    >
                      {savedSignature.signature_data}
                    </p>
                  )}
                </div>
              </div>
              {/* Signer Details */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Signed By</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{savedSignature.signer_name}</p>
                  {savedSignature.signer_title && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">{savedSignature.signer_title}</p>
                  )}
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{savedSignature.signer_email}</p>
                  {savedSignature.customer_po && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">Purchase order: {savedSignature.customer_po}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Signed On</p>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {savedSignature.signed_at ? formatDateTime(savedSignature.signed_at, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Audit Details - Only visible to internal staff */}
            {fromAdmin && (savedSignature.ip_address || savedSignature.user_agent) && (
              <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Audit Details</h4>
                <div className="space-y-1">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <span className="font-medium">IP Address:</span> {savedSignature.ip_address || 'N/A'}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 truncate" title={savedSignature.user_agent || ''}>
                    <span className="font-medium">User Agent:</span> {savedSignature.user_agent ? savedSignature.user_agent.substring(0, 80) + '...' : 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deposit Status Section - Only shown for accepted quotes with deposit requirement */}
      {deposit && (
        <div className={`mt-6 bg-white dark:bg-neutral-800 rounded-lg border ${
          deposit.status === 'paid'
            ? 'border-green-200 dark:border-green-800'
            : deposit.status === 'waived'
            ? 'border-neutral-200 dark:border-neutral-700'
            : 'border-amber-200 dark:border-amber-800'
        }`}>
          <div className={`px-6 py-4 border-b ${
            deposit.status === 'paid'
              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30'
              : deposit.status === 'waived'
              ? 'border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/30'
              : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className={`w-5 h-5 ${
                  deposit.status === 'paid'
                    ? 'text-green-600 dark:text-green-400'
                    : deposit.status === 'waived'
                    ? 'text-neutral-600 dark:text-neutral-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`} />
                <h2 className={`text-lg font-semibold ${
                  deposit.status === 'paid'
                    ? 'text-green-900 dark:text-green-300'
                    : deposit.status === 'waived'
                    ? 'text-neutral-900 dark:text-neutral-300'
                    : 'text-amber-900 dark:text-amber-300'
                }`}>
                  Deposit {deposit.status === 'paid' ? 'Paid' : deposit.status === 'waived' ? 'Waived' : 'Required'}
                </h2>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                deposit.status === 'paid'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                  : deposit.status === 'partial'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                  : deposit.status === 'waived'
                  ? 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300'
                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
              }`}>
                {deposit.status === 'paid' ? 'Paid' :
                 deposit.status === 'partial' ? 'Partial' :
                 deposit.status === 'waived' ? 'Waived' :
                 'Pending'}
              </span>
            </div>
          </div>
          <div className="p-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Deposit Amount</p>
                <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                  {formatPrice(deposit.depositAmount)}
                </p>
              </div>
              {deposit.amountPaid > 0 && (
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Amount Paid</p>
                  <p className="text-xl font-semibold text-green-600 dark:text-green-400">
                    {formatPrice(deposit.amountPaid)}
                  </p>
                </div>
              )}
            </div>

            {/* Payment Details - If paid */}
            {deposit.payments && deposit.payments.length > 0 && (
              <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Payment Details</p>
                {deposit.payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between py-2 px-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          {(() => {
                            const source = payment.paymentSource || payment.payment_source;
                            const cardType = payment.cardType || payment.card_type;
                            const last4Val = payment.cardLastFour || payment.card_last_four;
                            const isAch = source === 'one_time_ach' || cardType === 'ACH';
                            const last4 = last4Val ? ` ****${last4Val}` : '';
                            if (isAch) return `${formatPrice(payment.amount)} via ACH${last4}`;
                            return `${formatPrice(payment.amount)} via ${cardType || 'Card'}${last4}`;
                          })()}
                        </p>
                        {(payment.bankName || payment.bank_name) && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            {payment.bankName || payment.bank_name}
                          </p>
                        )}
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          {(() => {
                            const d = payment.paymentDate || payment.payment_date;
                            if (!d) return 'N/A';
                            return new Date(d).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          })()}
                        </p>
                      </div>
                    </div>
                    {(payment.transactionId || payment.transaction_id) && (
                      <span className="text-xs text-neutral-400 dark:text-neutral-500">
                        Trans: {payment.transactionId || payment.transaction_id}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Pay Deposit / Check options */}
            {(deposit.status === 'pending' || deposit.status === 'partial') && depositData?.depositsEnabled !== false && (
              <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                {deposit.sendingCheck ? (
                  <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <span className="text-blue-500">✉️</span>
                    <div>
                      <p className="font-medium text-blue-900 dark:text-blue-200 text-sm">Check payment indicated</p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                        Customer indicated they will mail a check. Order will not be processed until received and deposited.
                      </p>
                    </div>
                  </div>
                ) : !showCheckConfirmAuth ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                      {deposit.status === 'partial'
                        ? `Remaining balance: ${formatPrice(deposit.depositAmount - deposit.amountPaid)}`
                        : 'A deposit is required to proceed with this quote.'
                      }
                    </p>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <button
                        onClick={() => setShowCheckConfirmAuth(true)}
                        className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 underline underline-offset-2"
                      >
                        Sending a check?
                      </button>
                      <button
                        onClick={() => setShowPayDeposit(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
                      >
                        <CreditCard className="w-5 h-5" />
                        Pay Deposit {formatPrice(deposit.status === 'partial' ? deposit.depositAmount - deposit.amountPaid : deposit.depositAmount)}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div>
                      <p className="font-medium text-amber-900 dark:text-amber-200 mb-1">Mail your check to:</p>
                      {depositData?.companyInfo?.companyName && (
                        <p className="text-neutral-900 dark:text-white font-medium">{depositData.companyInfo.companyName}</p>
                      )}
                      {depositData?.companyInfo?.companyAddress ? (
                        <p className="text-neutral-700 dark:text-neutral-300 whitespace-pre-line text-sm">{depositData.companyInfo.companyAddress}</p>
                      ) : (
                        <p className="text-neutral-500 text-sm italic">Contact us for mailing address</p>
                      )}
                    </div>
                    <div className="bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 rounded p-3">
                      <p className="text-sm text-amber-900 dark:text-amber-200 font-medium">
                        ⚠️ Make check payable to {depositData?.companyInfo?.companyName || 'us'} for {formatPrice(deposit.status === 'partial' ? deposit.depositAmount - deposit.amountPaid : deposit.depositAmount)}.
                      </p>
                      <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                        Your order will not be placed until your check is received and successfully deposited.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={async () => {
                          setCheckSubmittingAuth(true);
                          try {
                            await depositsAPI.markSendingCheckAuth(id);
                            await refetchDeposit();
                            setShowCheckConfirmAuth(false);
                          } catch (e) {
                            console.error(e);
                          } finally {
                            setCheckSubmittingAuth(false);
                          }
                        }}
                        disabled={checkSubmittingAuth}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium transition-colors disabled:opacity-50"
                      >
                        {checkSubmittingAuth ? 'Saving...' : "Confirm — I'll send a check"}
                      </button>
                      <button
                        onClick={() => setShowCheckConfirmAuth(false)}
                        className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Waived message */}
            {deposit.status === 'waived' && deposit.waive_reason && (
              <div className="mt-4 p-3 bg-neutral-100 dark:bg-neutral-700/50 rounded-lg">
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  <span className="font-medium">Reason:</span> {deposit.waive_reason}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Activity - Only visible to internal staff */}
      {fromAdmin && (customerViews.length > 0 || customerViewStats.totalCustomerViews > 0) && (
        <div className="mt-6 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-500" />
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Customer Activity</h2>
                <span className="text-sm text-neutral-500 dark:text-neutral-400">(excludes internal staff views)</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-neutral-600 dark:text-neutral-300">
                <span><strong>{customerViewStats.totalCustomerViews || 0}</strong> views</span>
                <span><strong>{customerViewStats.uniqueCustomerViewers || 0}</strong> unique viewers</span>
              </div>
            </div>
          </div>
          <div className="p-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {customerViewStats.totalCustomerViews || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Total Customer Views</div>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="text-2xl font-bold text-neutral-900 dark:text-white">
                  {customerViewStats.uniqueCustomerViewers || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Unique Viewers</div>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold text-blue-600 dark:text-blue-400">
                  <Monitor className="w-5 h-5" />
                  {customerViewStats.portalViews || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Portal Views</div>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold text-purple-600 dark:text-purple-400">
                  <Link className="w-5 h-5" />
                  {customerViewStats.sharedLinkViews || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Shared Link Views</div>
              </div>
            </div>

            {/* Activity Timestamps */}
            {(customerViewStats.firstCustomerView || customerViewStats.lastCustomerView) && (
              <div className="flex flex-wrap gap-6 mb-6 text-sm">
                {customerViewStats.firstCustomerView && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-neutral-400" />
                    <span className="text-neutral-500 dark:text-neutral-400">First viewed:</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formatDateTime(customerViewStats.firstCustomerView, {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
                {customerViewStats.lastCustomerView && (
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-neutral-400" />
                    <span className="text-neutral-500 dark:text-neutral-400">Last viewed:</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formatDateTime(customerViewStats.lastCustomerView, {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Detailed View History Table */}
            {customerViews.length > 0 && (
              <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
                <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-4">View History</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                        <th className="pb-2 font-medium">Date/Time</th>
                        <th className="pb-2 font-medium">Viewer</th>
                        <th className="pb-2 font-medium">Source</th>
                        <th className="pb-2 font-medium">Device</th>
                        <th className="pb-2 font-medium">Browser / OS</th>
                        <th className="pb-2 font-medium">Engagement</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                      {customerViews.map((view) => (
                        <tr key={view.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                          <td className="py-3 text-neutral-900 dark:text-white whitespace-nowrap">
                            {formatDateTime(view.viewedAt, {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="py-3">
                            <div className="text-neutral-700 dark:text-neutral-200">
                              {view.viewerName || 'Anonymous'}
                            </div>
                            {view.viewerEmail && (
                              <div className="text-neutral-500 dark:text-neutral-400 text-xs">{view.viewerEmail}</div>
                            )}
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              view.viewSource === 'portal_direct'
                                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                : 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
                            }`}>
                              {view.viewSource === 'portal_direct' ? (
                                <><Monitor className="w-3 h-3" /> Portal</>
                              ) : (
                                <><Link className="w-3 h-3" /> Shared</>
                              )}
                            </span>
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              view.deviceType === 'mobile' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' :
                              view.deviceType === 'tablet' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' :
                              'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                            }`}>
                              {view.deviceType === 'mobile' && <Smartphone className="w-3 h-3 mr-1" />}
                              {view.deviceType === 'tablet' && <Tablet className="w-3 h-3 mr-1" />}
                              {view.deviceType === 'desktop' && <Monitor className="w-3 h-3 mr-1" />}
                              {view.deviceType || 'desktop'}
                            </span>
                          </td>
                          <td className="py-3 text-neutral-600 dark:text-neutral-300 text-xs">
                            <div>{view.browser} {view.browserVersion}</div>
                            <div className="text-neutral-400 dark:text-neutral-500">{view.os} {view.osVersion}</div>
                          </td>
                          <td className="py-3">
                            <div className="flex flex-wrap gap-1">
                              {view.timeOnPage && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {Math.floor(view.timeOnPage / 60)}m {view.timeOnPage % 60}s
                                </span>
                              )}
                              {view.scrolledToBottom && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                                  <Check className="w-3 h-3 mr-1" /> Read all
                                </span>
                              )}
                              {view.pdfDownloaded && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                  <Download className="w-3 h-3 mr-1" /> PDF
                                </span>
                              )}
                              {!view.timeOnPage && !view.scrolledToBottom && !view.pdfDownloaded && (
                                <span className="text-neutral-400 dark:text-neutral-500">-</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {customerViews.length >= 50 && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-4">
                    Showing last 50 customer views
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Questions & Comments - Portal users and Admin */}
      {(fromAdmin || canComment !== false) && (
        <div className="mt-6 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <button
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
            onClick={handleExpandComments}
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {fromAdmin ? 'Customer Questions' : 'Questions & Comments'}
              </h2>
              {fromAdmin && linkedTicket && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                  Ticket #{linkedTicket.id}
                </span>
              )}
              {fromAdmin && !linkedTicket && unreadCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
              <span>
                {fromAdmin && linkedTicket
                  ? `${ticketNotes.length} ${ticketNotes.length === 1 ? 'note' : 'notes'}`
                  : `${comments.length} ${comments.length === 1 ? 'message' : 'messages'}`
                }
              </span>
              {showCommentsSection ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </div>
          </button>

          {showCommentsSection && (
            <div className="border-t border-neutral-200 dark:border-neutral-700 p-6">
              {/* Linked Ticket Info - Admin only */}
              {fromAdmin && linkedTicket && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Linked to Ticket #{linkedTicket.id}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {linkedTicket.summary}
                        {linkedTicket.status?.name && ` • ${linkedTicket.status.name}`}
                      </p>
                    </div>
                    <a
                      href={`/tickets/${linkedTicket.id}?from=admin`}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Open Ticket →
                    </a>
                  </div>
                </div>
              )}

              {/* Admin with linked ticket: show CW ticket notes */}
              {fromAdmin && linkedTicket ? (
                ticketNotes.length === 0 ? (
                  <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
                    No notes on this ticket yet
                  </p>
                ) : (
                  <div className="space-y-4">
                    {ticketNotes.map((note) => (
                      <div
                        key={note.id}
                        className={`p-4 rounded-lg ${
                          note.member
                            ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 ml-8'
                            : 'bg-neutral-50 dark:bg-neutral-700/50 border border-neutral-100 dark:border-neutral-600'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {note.member ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-200 text-xs font-medium rounded-full">
                              <Building2 className="w-3 h-3" />
                              {note.member.name || note.member.identifier || 'Staff'}
                            </span>
                          ) : (
                            <>
                              <div className="w-6 h-6 bg-neutral-200 dark:bg-neutral-600 rounded-full flex items-center justify-center">
                                <User className="w-3 h-3 text-neutral-500 dark:text-neutral-300" />
                              </div>
                              <span className="font-medium text-sm text-neutral-900 dark:text-white">Customer</span>
                            </>
                          )}
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            {new Date(note._info?.dateEntered || note.dateCreated).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap">{note.text}</p>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                /* Comments display - for portal users and admin without ticket */
                comments.length === 0 && !fromAdmin ? (
                  <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
                    Have a question about this quote? Send us a message below.
                  </p>
                ) : comments.length === 0 ? (
                  <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
                    No questions or comments yet
                  </p>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div
                        key={comment.id}
                        className={`p-4 rounded-lg ${
                          comment.author_type === 'internal'
                            ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 ml-8'
                            : 'bg-neutral-50 dark:bg-neutral-700/50 border border-neutral-100 dark:border-neutral-600'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {comment.author_type === 'internal' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-200 text-xs font-medium rounded-full">
                                <Building2 className="w-3 h-3" />
                                {comment.author_name || 'Staff'}
                              </span>
                            ) : (
                              <>
                                <div className="w-6 h-6 bg-neutral-200 dark:bg-neutral-600 rounded-full flex items-center justify-center">
                                  <User className="w-3 h-3 text-neutral-500 dark:text-neutral-300" />
                                </div>
                                <span className="font-medium text-sm text-neutral-900 dark:text-white">
                                  {comment.author_name || 'Anonymous'}
                                </span>
                              </>
                            )}
                            {fromAdmin && comment.author_email && comment.author_type === 'customer' && (
                              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                ({comment.author_email})
                              </span>
                            )}
                            <span className="text-xs text-neutral-500 dark:text-neutral-400">
                              {new Date(comment.created_at).toLocaleString()}
                            </span>
                          </div>
                          {fromAdmin && comment.author_type === 'customer' && (
                            <button
                              onClick={() => setReplyingTo({
                                commentId: comment.id,
                                sessionId: comment.session_id,
                                authorName: comment.author_name
                              })}
                              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap">
                          {comment.message}
                        </p>
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* Reply/Question Form */}
              {fromAdmin ? (
                /* Admin reply form - show for tickets or when replying to comments */
                (linkedTicket || replyingTo) && (
                  <form onSubmit={handleSubmitReply} className="mt-4 p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600">
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                      {linkedTicket
                        ? 'Add a reply (will be added to the linked ticket)'
                        : `Replying to ${replyingTo?.authorName || 'customer'}`
                      }
                    </div>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your response..."
                      className="input min-h-[80px] mb-3"
                      required
                    />
                    <div className="flex justify-end gap-2">
                      {!linkedTicket && replyingTo && (
                        <button
                          type="button"
                          onClick={() => { setReplyingTo(null); setReplyText(''); }}
                          className="btn-secondary text-sm"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        className="btn-primary text-sm"
                        disabled={(linkedTicket ? addTicketNoteMutation.isPending : addCommentMutation.isPending) || !replyText.trim()}
                      >
                        {(linkedTicket ? addTicketNoteMutation.isPending : addCommentMutation.isPending) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Send className="w-4 h-4" /> Send Reply</>
                        )}
                      </button>
                    </div>
                    {(linkedTicket ? addTicketNoteMutation.error : addCommentMutation.error) && (
                      <p className="text-sm text-red-600 mt-2">
                        {(linkedTicket ? addTicketNoteMutation.error : addCommentMutation.error)?.response?.data?.error || 'Failed to send reply'}
                      </p>
                    )}
                  </form>
                )
              ) : (
                /* Portal customer question form */
                <form onSubmit={handleSubmitReply} className="mt-4 p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Ask a question or leave a comment..."
                    className="input min-h-[80px] mb-3"
                    required
                  />
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="btn-primary text-sm"
                      disabled={addPortalCommentMutation.isPending || !replyText.trim()}
                    >
                      {addPortalCommentMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Send className="w-4 h-4" /> Send Message</>
                      )}
                    </button>
                  </div>
                  {addPortalCommentMutation.error && (
                    <p className="text-sm text-red-600 mt-2">
                      {addPortalCommentMutation.error?.response?.data?.error || 'Failed to send message'}
                    </p>
                  )}
                </form>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex flex-wrap gap-3 justify-end">
          {isInternalStaff && isEditable && (
            <button
              onClick={handleShare}
              disabled={shareMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium transition-colors disabled:opacity-50"
            >
              {shareMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Share2 className="w-5 h-5" />
              )}
              Share
            </button>
          )}
          {canDownloadPdf && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium transition-colors disabled:opacity-50"
            >
              {downloadingPdf ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              Download PDF
            </button>
          )}
          {canApprove && isEditable && !isExpired && (
            <>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors"
              >
                <X className="w-5 h-5" />
                Decline Quote
              </button>
              <button
                onClick={() => setShowApproveModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
              >
                <Check className="w-5 h-5" />
                Approve Quote
              </button>
            </>
          )}
        </div>
      </div>

      {/* Approve Modal with E-Signature */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-lg w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                <FileSignature className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">Approve Quote</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Electronic signature required</p>
              </div>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg mb-6">
              <p className="text-green-800 dark:text-green-200">
                You are about to approve this quote for a total of{' '}
                <span className="font-semibold">
                  {formatPrice(dueNow())}
                </span>
                {presentation?.totals.recurring > 0 && (
                  <> plus <RecurringPhrase totals={presentation.totals} strong /></>
                )}
              </p>
            </div>

            <div className="mb-6">
              <PurchaseOrderField
                mode={poMode}
                value={customerPO}
                onChange={setCustomerPO}
                inputClassName="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* E-Signature Capture */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                Your Signature <span className="text-red-500">*</span>
              </label>
              <SignatureCapture
                onChange={handleSignatureChange}
                signerName=""
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Word for word what the server stores as consent (QUOTE_CONSENT.portal in
                services/contracts/signing.service.js); change both together. */}
            <label className="mb-6 flex items-start gap-3 text-sm text-neutral-700 dark:text-neutral-300">
              <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} className="mt-0.5 h-4 w-4" />
              <span>I am authorized to accept this quote on behalf of my organization. I agree to sign electronically, and my electronic signature has the same effect as my handwritten signature.</span>
            </label>

            {approveMutation.isError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {approveMutation.error?.response?.data?.error || 'Failed to approve quote'}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setSignature({ type: null, data: '', isValid: false });
                }}
                disabled={approveMutation.isPending}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={approveMutation.isPending || !signature.isValid || !consented || (poMode === 'required' && !customerPO.trim())}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approveMutation.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Sign & Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">Decline Quote</h3>
            <p className="text-neutral-600 dark:text-neutral-300 mb-4">
              Are you sure you want to decline this quote? This action cannot be undone.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Let us know why you're declining..."
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {rejectMutation.isError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {rejectMutation.error?.response?.data?.error || 'Failed to decline quote'}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={rejectMutation.isPending}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejectMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                {rejectMutation.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <X className="w-5 h-5" />
                    Decline
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center">
                <Link className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">Share Quote</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {shareLink ? 'Share this link to view the quote' : 'Generate a shareable link'}
                </p>
              </div>
            </div>

            {!shareLink ? (
              <>
                {/* Expiration selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Link Expires In
                  </label>
                  <select
                    value={shareExpiresDays}
                    onChange={(e) => setShareExpiresDays(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                    <option value={180}>180 days</option>
                    <option value={365}>1 year</option>
                  </select>
                </div>

                {/* Terms and Conditions customization */}
                <div className="mb-4">
                  <button
                    onClick={() => setShowTermsEditor(!showTermsEditor)}
                    className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    <FileText className="w-4 h-4" />
                    Customize Terms & Conditions
                    {showTermsEditor ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {showTermsEditor && (
                    <div className="mt-3 space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={useCustomTerms}
                          onChange={(e) => {
                            setUseCustomTerms(e.target.checked);
                            // If enabling and customTerms is empty, populate with default
                            if (e.target.checked && !customTerms && defaultTerms) {
                              setCustomTerms(defaultTerms);
                            }
                          }}
                          className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-neutral-600 dark:text-neutral-300">
                          Use custom terms for this quote
                        </span>
                      </label>

                      <textarea
                        value={useCustomTerms ? customTerms : defaultTerms}
                        onChange={(e) => setCustomTerms(e.target.value)}
                        disabled={!useCustomTerms}
                        rows={6}
                        placeholder="Enter custom terms and conditions..."
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          useCustomTerms
                            ? 'border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white'
                            : 'border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300'
                        }`}
                      />

                      {!useCustomTerms && defaultTerms && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Default terms shown above. Check the box to customize.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg mb-4">
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">
                    Anyone with this link will be able to view the quote details, line items, and totals without logging in.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateShareLink}
                    disabled={shareMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50"
                  >
                    {shareMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Share2 className="w-4 h-4" />
                    )}
                    Generate Link
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Generated link display */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Shareable Link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareLink.url}
                      className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg text-sm"
                    />
                    <button
                      onClick={handleCopyLink}
                      className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        linkCopied
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      {linkCopied ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Expires: {shareLink.expiresAt
                        ? new Date(shareLink.expiresAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : `${shareExpiresDays} days`}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Anyone with this link can view the quote without logging in.
                  </p>
                </div>

                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareLink(null);
                    setLinkCopied(false);
                  }}
                  className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Pay Deposit Modal */}
      {showPayDeposit && deposit && (
        <PayDepositModal
          isOpen={showPayDeposit}
          onClose={() => setShowPayDeposit(false)}
          deposit={deposit}
          quoteName={quote?.name || `Quote #${id}`}
          paymentSettings={paymentSettings}
          onSuccess={() => {
            setShowPayDeposit(false);
            refetchDeposit();
          }}
        />
      )}
    </div>
  );
}
